import { createAuthClient } from '@gastrosoft/shared/auth';
import type { User } from '@/types';
import { tokenManager } from '@/app/services/tokenManager';
import client from '@/graphql/client';
import { GetUserDocument, type GetUserQuery } from '@/generated/root-types';

export { EmailNotConfirmedError } from '@gastrosoft/shared/auth';

const AUTH_ENDPOINT = import.meta.env.VITE_AUTH_ENDPOINT;

/** JWT-Payload (Relevant für User-Fallback). */
interface JwtPayload {
  sub?: string;
  name?: string;
  email?: string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'?: string;
  TenantId?: string;
  ResellerId?: string;
  role?: string | string[];
}

function decodeJwtPayload(token: string): JwtPayload {
  try {
    const base64 = token.split('.')[1];
    if (!base64) return {};
    return JSON.parse(atob(base64)) as JwtPayload;
  } catch {
    return {};
  }
}

/** User aus JWT bauen, wenn GetUser fehlschlägt (z. B. andere API / Audience). */
function fallbackUserFromToken(token: string): User {
  const p = decodeJwtPayload(token);
  const email =
    p.email ??
    p['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ??
    p.name ??
    '';
  const roles = Array.isArray(p.role) ? p.role : p.role ? [p.role] : [];
  return {
    id: p.sub ?? '',
    email,
    userName: p.name ?? email,
    role: roles,
    resellerId: p.ResellerId ?? null,
  };
}

function mapUser(data: NonNullable<GetUserQuery['user']>): User {
  return {
    id: data.sub,
    email: data.email ?? '',
    userName:
      ([data.givenName, data.familyName].filter(Boolean).join(' ').trim() || data.email) ?? '',
    firstName: data.givenName ?? undefined,
    lastName: data.familyName ?? undefined,
    role: data.role ?? [],
    resellerId: data.resellerId ?? null,
  };
}

export const authClient = createAuthClient<GetUserQuery>({
  authEndpoint: AUTH_ENDPOINT,
  client,
  getUserDocument: GetUserDocument,
  tokenManager,
  mapUser,
  fallbackUserFromToken,
});
